import { Spin, Tag, Tooltip } from 'antd';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  LoadingOutlined,
  TranslationOutlined,
} from '@ant-design/icons';
import type { DocumentTranslation } from '@/pages/application/apis/applicationApi';

interface Props {
  translation?: DocumentTranslation;
  /** Opens the translated PDF via an authenticated fetch (not a bare href). */
  onOpen?: (translation: DocumentTranslation) => void;
  /** True while this row's PDF is being fetched. */
  isOpening?: boolean;
}

const TranslationBadge = ({ translation, onOpen, isOpening }: Props) => {
  if (!translation) {
    return (
      <Tag
        icon={<TranslationOutlined />}
        bordered={false}
        style={{ margin: 0, fontSize: 11, opacity: 0.75 }}
      >
        Not translated
      </Tag>
    );
  }

  switch (translation.status) {
    case 'QUEUED':
      return (
        <Tag
          icon={<ClockCircleOutlined />}
          bordered={false}
          color="default"
          style={{ margin: 0, fontSize: 11 }}
        >
          Queued…
        </Tag>
      );
    case 'RUNNING':
      return (
        <Tag
          icon={<Spin size="small" style={{ marginRight: 4 }} />}
          bordered={false}
          color="processing"
          style={{ margin: 0, fontSize: 11 }}
        >
          Translating…
        </Tag>
      );
    case 'COMPLETED': {
      const toReview = translation.lowConfidenceCount;
      const icon = isOpening ? (
        <LoadingOutlined />
      ) : toReview ? (
        <TranslationOutlined />
      ) : (
        <CheckCircleFilled />
      );
      return (
        <Tooltip
          title={
            toReview
              ? `${toReview} low-confidence field${
                  toReview === 1 ? '' : 's'
                } to review · opens the translated PDF`
              : 'Opens the translated PDF'
          }
        >
          <Tag
            icon={icon}
            bordered={false}
            color={toReview ? 'gold' : 'success'}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(translation);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onOpen?.(translation);
              }
            }}
            style={{ margin: 0, fontSize: 11, cursor: 'pointer' }}
          >
            {toReview ? `Translated · ${toReview} to review` : 'Translated'}
          </Tag>
        </Tooltip>
      );
    }
    case 'FAILED':
      return (
        <Tooltip title={translation.error || 'Translation failed'}>
          <Tag
            icon={<CloseCircleFilled />}
            bordered={false}
            color="error"
            style={{ margin: 0, fontSize: 11 }}
          >
            Failed
          </Tag>
        </Tooltip>
      );
    default:
      return null;
  }
};

export default TranslationBadge;
